import { useEffect, useState } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useFirebaseChat } from './useFirebaseChat';
import { useConversationMessages } from './useConversationMessages';
import { MessageList } from './MessageList';
import { Composer } from './Composer';
import { fetchMyConversation, markConversationRead, sendMessage } from './api';
import type { ChatConversation } from './types';

const firebaseEnabled = !!import.meta.env.VITE_FIREBASE_DATABASE_URL;

function normalizeRole(raw: string | undefined): string {
  return (raw ?? '').toLowerCase().replace(/\s+/g, '_');
}

export function ChatWidget() {
  const user = useAuthStore((s) => s.user);
  const role = normalizeRole(user?.role);
  const visible = !!user && role !== 'super_admin' && firebaseEnabled;

  const [open, setOpen] = useState(false);
  const [conversation, setConversation] = useState<ChatConversation | null>(null);

  const fb = useFirebaseChat(open);
  const { messages, loadOlder, hasMore, loadingOlder } = useConversationMessages({
    db: fb.db,
    conversationId: conversation?.id ?? null,
    enabled: open,
  });

  useEffect(() => {
    if (!open || conversation) return;
    let cancelled = false;
    fetchMyConversation()
      .then((c) => {
        if (!cancelled && c) setConversation(c);
      })
      .catch(() => {
        // ignore — the first send will create the conversation
      });
    return () => {
      cancelled = true;
    };
  }, [open, conversation]);

  useEffect(() => {
    if (!open || !conversation) return;
    markConversationRead(conversation.id).catch(() => {});
  }, [open, conversation, messages.length]);

  const onSend = async (body: string) => {
    const wasFirstSend = !conversation;
    await sendMessage({ conversationId: conversation?.id, body });
    if (wasFirstSend) {
      try {
        const fresh = await fetchMyConversation();
        if (fresh) {
          setConversation(fresh);
          // Re-mint the Firebase custom token so it carries the new
          // conversationId claim required to subscribe to the RTDB tail.
          await fb.refresh();
        }
      } catch {
        // ignored
      }
    }
  };

  if (!visible) return null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 inline-flex h-14 w-14 items-center justify-center rounded-full bg-orange-500 text-white shadow-lg hover:bg-orange-600 transition"
        aria-label="Open support chat"
        data-test-id="chat-widget-fab"
      >
        <MessageCircle className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-50 flex h-[520px] w-[360px] flex-col rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden"
      data-test-id="chat-widget-panel"
    >
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div>
          <div className="font-semibold text-sm">Support</div>
          <div className="text-xs text-gray-500">We typically reply within minutes</div>
        </div>
        <button
          onClick={() => setOpen(false)}
          aria-label="Close"
          className="text-gray-500 hover:text-gray-700"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      {fb.error && (
        <div className="bg-red-50 text-red-700 text-xs p-2">
          Chat temporarily unavailable. Please try again.
        </div>
      )}
      <MessageList
        messages={messages}
        selfUserId={user?.id ?? ''}
        onLoadOlder={loadOlder}
        hasMore={hasMore}
        loadingOlder={loadingOlder}
      />
      <Composer onSend={onSend} disabled={!fb.ready} />
    </div>
  );
}
